import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { useT } from '../i18n/LanguageContext';
import { useDestinations } from '../hooks/use-activities';

export default function TopDestinations() {
  const t = useT();
  const { data } = useDestinations();
  const destinations = data || [];
  const scrollerRef = useRef(null);

  const scrollByCard = (direction) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector('a');
    const amount = (card?.offsetWidth || 320) + 16;
    el.scrollBy({ left: direction * amount, behavior: 'smooth' });
  };

  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
          <div>
            <p className="text-[#c8a25a] text-sm font-semibold tracking-widest uppercase mb-2">{t('destinations.eyebrow')}</p>
            <h2 className="text-4xl md:text-5xl font-bold text-[#14213d]">
              {t('destinations.title')}<br /><span className="italic font-medium">{t('destinations.titleAccent')}</span>
            </h2>
          </div>
          <div className="flex items-center gap-3 self-start md:self-end">
            <Link to="/destinations" className="text-[#1fa5a3] font-semibold hover:text-[#c8a25a] transition-colors">
              {t('destinations.viewAll')} →
            </Link>
            {/* Mouse users have no swipe gesture, so give them prev/next. Touch
                users on any breakpoint just swipe the row directly. */}
            <div className="hidden sm:flex items-center gap-2">
              <button
                type="button"
                onClick={() => scrollByCard(-1)}
                aria-label={t('common.previous')}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 text-[#14213d] transition hover:border-[#1fa5a3] hover:text-[#1fa5a3]"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => scrollByCard(1)}
                aria-label={t('common.next')}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 text-[#14213d] transition hover:border-[#1fa5a3] hover:text-[#1fa5a3]"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Horizontal swipe-snap row on every breakpoint — desktop gets the same
            slider (plus prev/next buttons above) instead of a grid, so it never
            stacks into a tall scroll wall on mobile either. */}
        <div
          ref={scrollerRef}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 -mx-5 px-5 sm:mx-0 sm:px-0 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        >
          {destinations.map((d) => {
            const activityCount = d.activity_count;
            const countLabel = activityCount === 1 ? t('destinations.activityCountOne') : t('destinations.activityCount');
            return (
            <Link
              key={d.slug}
              to={`/destinations/${d.slug}`}
              className="group relative min-h-[210px] w-[78%] shrink-0 snap-start overflow-hidden rounded-xl bg-[#0b1c26] aspect-[16/10] shadow-[0_16px_35px_-24px_rgba(11,33,61,0.55)] sm:w-[300px] lg:w-[340px]"
            >
              <img
                src={d.image}
                alt={d.name}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#071a2b]/95 via-[#071a2b]/20 to-transparent transition-colors group-hover:from-[#071a2b]" />
              <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-5 text-white">
                <div className="min-w-0">
                  <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-white/75">
                    <MapPin className="h-3.5 w-3.5 text-[#c8a25a]" /> {t('destinations.eyebrow')}
                  </div>
                  <h3 className="break-words text-2xl font-bold leading-tight">{d.name}</h3>
                  {activityCount ? (
                    <p className="mt-1 text-sm text-white/75">{activityCount} {countLabel}</p>
                  ) : (
                    <p className="mt-1 text-sm text-white/75">{t('places.comingSoon')}</p>
                  )}
                </div>
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border border-white/30 bg-white/10 backdrop-blur transition-all group-hover:border-[#c8a25a] group-hover:bg-[#c8a25a]">
                  <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
